import ProductImages from './ProductImages';
import Quantity from '../Quantity';
import { closeModal } from '../../helpers/modals';
import {
  cartApi,
  getTranslation,
  showQuickshop,
  updateCart,
} from '../../helpers';

const selectors = {
  form: '[data-product-form]',
  addToCart: '[data-add-to-cart]',
  addToCartText: '[data-add-to-cart-text]',
  productSelect: '[data-product-select]',
  optionInput: '[name^="options"]',
  modalContent: 'quickshop-content',
  modalProduct: '[data-product]',
  productImage: '[data-product-image]',
  mainCarousel: '[data-image-carousel]',
  thumbnailCarousel: '[data-thumbnail-carousel]',
  complementaryProducts: '[data-complementary-products]',
};

const rootMargin = '0px 0px 200px 0px';
const limit = 4;

/**
 * The wrapper class for PDP components
 *
 * @export
 * @class Product
 */
export default class Product {
  /**
   * Creates an instance of Product.
   *
   * @param {HTMLElement} el The container of this product
   * @param {Object} config Settings object
   * @param {String} config Section ID
   */
  constructor(el, config = {}) {
    this.el = el;
    this.section = this._getSectionElement(this.el);
    this.sectionId = this.section.id.replace('shopify-section-', '');
    this.config = Object.assign(
      {
        isQuickShop: false,
        isFeaturedProduct: false,
        quickShopUrl: '',
        modal: null,
      },
      config,
    );

    this.formElement = this.el.querySelector(selectors.form);
    this.addToCartButton = this.formElement.querySelector(selectors.addToCart);
    this.addToCartText = this.formElement.querySelector(
      selectors.addToCartText,
    );
    this.optionInputs = this.formElement.querySelectorAll(
      selectors.optionInput,
    );

    this.complementaryProducts = this.el.querySelector(
      selectors.complementaryProducts,
    );

    this._initComponents();
  }

  /**
   * Init all components that make up the product
   */
  _initComponents() {
    this.productImages = new ProductImages(this.el);
    this._initProductForm();

    if (this.complementaryProducts) {
      this._initComplementaryProducts();
    }
  }

  _initProductForm() {
    this.addToCartButton?.addEventListener(
      'click',
      this._onAddToCart.bind(this),
    );

    this.optionInputs?.forEach(optionInput => {
      optionInput.addEventListener('change', this._onOptionChange.bind(this));
    });

    new Quantity(this.formElement);
  }

  _initComplementaryProducts() {
    this.productId = this.complementaryProducts.dataset.productId;
    // TODO: create lazy data pattern as helper
    const observer = new IntersectionObserver(
      this._fetchComplementaryProducts,
      {
        rootMargin,
      },
    );

    observer.observe(this.complementaryProducts);
  }

  _getSectionElement = el => {
    return el.closest('.shopify-section');
  };

  _fetchComplementaryProducts = (entries, observer) => {
    if (!entries[0].isIntersecting) {
      return;
    }

    observer.unobserve(this.complementaryProducts);
    const url = `${Shopify.routes.root}recommendations/products?section_id=${this.sectionId}&limit=${limit}&product_id=${this.productId}&intent=complementary`;

    fetch(url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const complementary = html.querySelector(
          selectors.complementaryProducts,
        );

        if (complementary && complementary.innerHTML.trim().length) {
          this.complementaryProducts.innerHTML = complementary.innerHTML;
        }
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
  };

  _onAddToCart = e => {
    e.preventDefault();

    const formData = new FormData(this.formElement);
    const data = cartApi.formatFormData(formData);

    this._toggleLoading(true, getTranslation('products.adding'));

    cartApi
      .add({ items: [data] })
      .then(response => {
        this._toggleLoading(false, getTranslation('products.added'));
        this._reRenderSection(this.formElement.dataset.productUrl);

        if (response.sections) {
          updateCart({ sections: response.sections, openCart: true });
        }

        if (this.config.isQuickShop) {
          closeModal('quickshop');
        }
      })
      .catch(error => {
        console.error(error);
        this._toggleLoading(false, getTranslation('products.error'));
      });
  };

  _getSelectedOptions = () => {
    return Array.from(this.optionInputs)
      .reduce((arr, input) => {
        if (
          input instanceof HTMLInputElement &&
          input.type === 'radio' &&
          input.checked
        ) {
          arr.push(input.dataset.valueId);
        }

        if (input instanceof HTMLSelectElement) {
          const selectedOption = input.options[input.selectedIndex];
          arr.push(selectedOption.dataset.valueId);
        }

        return arr;
      }, [])
      .join(',');
  };

  _onOptionChange = e => {
    let productUrl = this.formElement.dataset.productUrl;
    let variantId;
    let isCombinedListing = false;

    if (e.target instanceof HTMLInputElement && e.target.type === 'radio') {
      // Check if changed option has associated product URL [Combined Listings]
      const { variantId: optionVariantId, productUrl: optionProductUrl } =
        e.target.dataset;

      if (optionProductUrl) {
        productUrl = optionProductUrl;
        isCombinedListing = true;
      }

      if (optionVariantId) {
        variantId = optionVariantId;
      }
    }

    if (e.target instanceof HTMLSelectElement) {
      const { productUrl: optionProductUrl, variantId: optionVariantId } =
        e.target.options[e.target.selectedIndex].dataset;

      if (optionProductUrl) {
        productUrl = optionProductUrl;
        isCombinedListing = true;
      }

      if (optionVariantId) {
        variantId = optionVariantId;
      }
    }

    let newURL;

    if (isCombinedListing) {
      newURL = productUrl;
    } else {
      newURL = new URL(window.location.href);
      newURL.searchParams.set('variant', variantId);
      newURL = newURL.toString();
    }

    this._toggleLoading(true, getTranslation('products.loading'));
    this._reRenderSection(productUrl);

    if (!this.config.isQuickShop && !this.config.isFeaturedProduct) {
      this._appendHistory(newURL);
    }
  };

  _reRenderSection = url => {
    if (this.config.isQuickShop) {
      url = this.config.quickShopUrl;
    } else {
      url = `${url}?section_id=${this.sectionId}`;
    }

    const selectedOptions = this._getSelectedOptions();

    if (selectedOptions.length) {
      url += `&option_values=${selectedOptions}`;
    }

    fetch(url)
      .then(response => response.text())
      .then(data => {
        if (this.config.isQuickShop) {
          const quickshopHtml = showQuickshop(data);
          const quickShopContent = document.getElementById(
            selectors.modalContent,
          );
          quickShopContent.innerHTML = quickshopHtml;

          const product = quickShopContent.querySelector(
            selectors.modalProduct,
          );

          // Create a new Product instance for the requested quickshop product
          new Product(product, this.config);
        } else {
          this.section.outerHTML = data;
          this.section = document.getElementById(
            `shopify-section-${this.sectionId}`,
          );
          const product = this.section.querySelector('[data-product]');
          new Product(product, this.config);
        }
      });
  };

  _appendHistory(url) {
    window.history.pushState({ path: url }, '', url);
  }

  _toggleLoading(loading = false, buttonText) {
    if (loading) {
      this.formElement.classList.add('loading');
      this.addToCartButton.disabled = true;
      this.optionInputs?.forEach(optionInput => (optionInput.disabled = true));
    } else {
      this.formElement.classList.remove('loading');
      this.addToCartButton.removeAttribute('disabled');
      this.optionInputs?.forEach(optionInput =>
        optionInput.removeAttribute('disabled'),
      );
    }

    if (buttonText) {
      this.addToCartText.textContent = buttonText;
    }
  }
}
